import { fetchUserClasses } from '@amplify/amp-api';
import { createApi } from '@reduxjs/toolkit/query/react';

import { Class } from 'src/interfaces/class.types';
import {
  OnboardingUserInfoResponse,
  User,
  UsersFreeIndicatorResponse,
} from 'src/interfaces/onboarding.types';
import { loggerService } from 'src/services/logger.service';
import { baseQuery, useAppDispatch } from 'src/store/redux';
import { sortClassesByGradesInDesc } from 'src/utils/class-picker.utils';

import { OnboardingQueryTagTypes } from './constants';

export const onboardingApi = createApi({
  reducerPath: 'onboarding',
  baseQuery: baseQuery('onboarding'),
  tagTypes: [OnboardingQueryTagTypes.CLASSES],
  endpoints: (builder) => ({
    getUserClasses: builder.query<Class[], string>({
      queryFn: async (userBk) => {
        try {
          const userClasses = await fetchUserClasses(userBk, "status='active'");
          return {
            data: sortClassesByGradesInDesc(userClasses.classes),
          };
        } catch (e) {
          loggerService.logError(
            'Something went wrong during user classes fetch',
            e,
          );

          return {
            error: e,
          };
        }
      },
      providesTags: [OnboardingQueryTagTypes.CLASSES],
    }),
    getOnboardingUserInfo: builder.query<User | undefined, string>({
      query: (userBk) => `ims/oneroster/v1p1/users/${userBk}`,
      transformResponse: (res: OnboardingUserInfoResponse) => res.user,
      async onQueryStarted(userBk, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          loggerService.logError('Onboarding user info loading error', e);
        }
      },
    }),
    getUsersFreeIndicator: builder.query<UsersFreeIndicatorResponse, void>({
      query: () => 'users/freeindicator',
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          loggerService.logError('Users free indicator loading error', e);
        }
      },
    }),
  }),
});

export const useUserClassesRefetchWithCurrentArgs = () => {
  const dispatch = useAppDispatch();

  return () => {
    dispatch(
      onboardingApi.util.invalidateTags([OnboardingQueryTagTypes.CLASSES]),
    );
  };
};

// do not use useGetOnboardingUserInfoQuery directly - use useOnboardingUserInfo instead
export const {
  useGetUserClassesQuery,
  useGetOnboardingUserInfoQuery,
  useGetUsersFreeIndicatorQuery,
} = onboardingApi;
